import { BookmarkPlus } from "lucide-react";

import { knowledgeService } from "@/entities/knowledge/api/knowledgeService";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { Button } from "@/shared/ui/button";

import { canNominateToKb } from "../lib/kbNominate";

/**
 * 「这个好,入库」—— 把一件海报/视频产物提名进知识库(E4/G1c,design §8)。
 *
 * **为什么这个按钮必须存在**:后端那口(`POST /tenant/knowledge/deliverables/nominate`)D 批就通了,
 * ai 侧的 `nominate-deliverable` 也通了,但 console 里**一个调用点都没有** —— 于是设计文档 §8 那句
 * 「军师产出 → 用户"这个好,入库"」在生产里是不可达的。这是全 program 复盘里「接口在没人调」的一例:
 * 三段都建好了,就差把它接到人能点的地方。
 *
 * **提名 ≠ 入库**(§7.1):产物恒 pending,进不了 live 检索 —— 污染半径 0,所以租户员工就能提。
 * 真正的门在审批台(平台超管代客审)。文案上要说清这一点,否则用户点完以为客服立刻就会发这张图了。
 */
export function NominateToKbButton({
  taskId,
  asset,
  disabled,
}: {
  taskId: number;
  /** 只取 seq/status —— 海报与视频的产物 DTO 形状不同,别把整个 DTO 拖进来。 */
  asset: { seq: number; status?: string | null };
  disabled?: boolean;
}) {
  const nominate = useMutationWithToast({
    mutationFn: () => knowledgeService.nominateDeliverable({ taskId, seq: asset.seq }),
    // ★别写成"已入库":它现在是 pending,客服还检索不到。用户按这句话去问客服拿不到图,
    //   只会当成系统坏了(而实际上是在等审)。
    successToast: {
      title: "已提交入库申请",
      description: "内容管理员审核通过后,客服才会把它发给客户。",
    },
    errorToast: "提交入库申请失败,可重试",
    invalidateKeys: [["knowledge-documents"], ["kb-doc-review"]],
  });

  if (!canNominateToKb(asset)) {
    return null;
  }

  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => nominate.mutate(undefined)}
      disabled={disabled || nominate.isPending}
      title="把这件产物提交给内容管理员审核,通过后客服就能把它发给客户"
    >
      <BookmarkPlus className="mr-1 h-4 w-4" />
      这个好,入库
    </Button>
  );
}
